import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Trophy, TrendingUp, TrendingDown, Sparkles, Users2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

const monthsOptions = [
  { value: 1, label_ca: "Aquest mes", label_es: "Este mes" },
  { value: 3, label_ca: "Trimestre", label_es: "Trimestre" },
  { value: 12, label_ca: "12 mesos", label_es: "12 meses" },
];

/**
 * Top suppliers widget shown on the main Dashboard. Reads
 * /api/suppliers/stats/top and renders a Pareto-style bar list with
 * percentage of total and variation vs. previous period.
 */
export default function TopSuppliersWidget() {
  const { lang } = useI18n();
  const [months, setMonths] = useState(1);
  const [data, setData] = useState({ rows: [], total_current: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers/stats/top", {
        params: { limit: 10, months },
      });
      setData(data);
    } catch {
      setData({ rows: [], total_current: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [months]); // eslint-disable-line

  const maxTotal = data.rows[0]?.total || 1;

  return (
    <div className="card-surface p-5" data-testid="top-suppliers-widget">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Trophy size={14} className="text-amber-300" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">
              {lang === "es" ? "Top 10 proveedores" : "Top 10 proveïdors"}
            </h3>
            <div className="text-[10px] text-slate-500">
              {lang === "es" ? "Por gasto y variación vs. periodo anterior" : "Per despesa i variació vs. període anterior"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {monthsOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMonths(opt.value)}
              className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                months === opt.value
                  ? "bg-blue-500/20 text-blue-200 border border-blue-500/40"
                  : "text-slate-400 hover:bg-white/5 border border-transparent"
              }`}
              data-testid={`top-suppliers-period-${opt.value}`}
            >
              {lang === "es" ? opt.label_es : opt.label_ca}
            </button>
          ))}
        </div>
      </div>

      {loading && data.rows.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
          {lang === "es" ? "Cargando..." : "Carregant..."}
        </div>
      ) : data.rows.length === 0 ? (
        <div className="h-24 flex flex-col items-center justify-center text-center">
          <Users2 size={20} className="text-slate-600 mb-2" />
          <p className="text-xs text-slate-500">
            {lang === "es"
              ? "Sin facturas en este periodo"
              : "Sense factures en aquest període"}
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 pb-3 border-b border-white/5 flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {lang === "es" ? "Total gasto" : "Total despesa"}
            </span>
            <span className="text-lg font-bold text-white font-mono">
              {fmtEur(data.total_current)}
            </span>
          </div>
          <div className="space-y-2.5">
            {data.rows.map((r, idx) => {
              const barPct = (r.total / maxTotal) * 100;
              return (
                <Link
                  key={r.supplier_nif + idx}
                  to="/app/suppliers"
                  className="block hover:bg-white/[0.03] -mx-2 px-2 py-1 rounded-lg transition-colors group"
                  data-testid={`top-supplier-${idx}`}
                >
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center bg-white/5 text-slate-300 flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="flex-1 truncate font-medium text-slate-200 group-hover:text-white">
                      {r.supplier_name || r.supplier_nif || "—"}
                    </span>
                    <span className="text-slate-400 font-mono text-[10px]">
                      {r.count} {lang === "es" ? "fact." : "fact."}
                    </span>
                    <span className="text-white font-mono font-semibold w-20 text-right">
                      {fmtEur(r.total)}
                    </span>
                    <DeltaBadge delta_pct={r.delta_pct} is_new={r.is_new} lang={lang} />
                  </div>
                  <div className="relative h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-[10px] text-slate-500 font-mono">
                      {r.pct_of_total}% {lang === "es" ? "del total" : "del total"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            to="/app/suppliers"
            className="mt-3 pt-3 border-t border-white/5 flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-white transition-colors"
            data-testid="top-suppliers-view-all"
          >
            {lang === "es" ? "Ver todos los proveedores" : "Veure tots els proveïdors"}
            <ChevronRight size={12} />
          </Link>
        </>
      )}
    </div>
  );
}

function DeltaBadge({ delta_pct, is_new, lang }) {
  if (is_new) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-500/15 text-violet-300 border border-violet-500/30 w-16 justify-center flex-shrink-0">
        <Sparkles size={9} />
        {lang === "es" ? "Nuevo" : "Nou"}
      </span>
    );
  }
  if (!delta_pct || Math.abs(delta_pct) < 0.5) {
    return <span className="w-16 text-[10px] text-slate-500 text-center font-mono flex-shrink-0">—</span>;
  }
  const positive = delta_pct > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold w-16 justify-center flex-shrink-0 ${
      positive ? "bg-rose-500/10 text-rose-300" : "bg-emerald-500/10 text-emerald-300"
    }`}>
      <Icon size={9} />
      {positive ? "+" : ""}{delta_pct}%
    </span>
  );
}
